/**
 * Spreadsheet-related constants. Types live in spreadsheetTypes.ts.
 */

/** Message when a locked column is modified. */
export const LOCKED_COLUMN_ERROR_MSG = 'Locked columns cannot be modified.';

/** Maximum rows and columns allowed in the spreadsheet. */
export const MAX_SPREADSHEET_ROWS = 10000;
export const MAX_SPREADSHEET_COLUMNS = 50;

/** List display separator when showing Python/list-style values in a cell. */
export const LIST_DISPLAY_SEPARATOR = ', ';

/** Default cell font for measuring text width (matches Univer cell rendering). */
export const CELL_MEASURE_FONT =
  '15px Arial, "Helvetica Neue", Helvetica, sans-serif';
export const COLUMN_WIDTH_PADDING = 24;
export const COLUMN_WIDTH_MIN = 100;
export const COLUMN_WIDTH_MAX = 500;
/** Extra width for AI columns (header text + gap + icon). */
export const AI_COLUMN_EXTRA_WIDTH = 12 + 14 + 4;

export const FAVICON_SIZE = 16;
export const FIRST_COL_FAVICON_PADDING = 6;
export const FAVICON_MIN_SIZE = 1;

/** Debounce delay (ms) before running unified spreadsheet sync (up and down). Sync runs after user stops typing (cell edit, undo, redo, sort, paste). */
export const SYNC_ON_CELL_EDIT_DEBOUNCE_MS = 400;

/** List display separator when normalizing list values for payload (matches Spreadsheet display). */
export const LIST_PAYLOAD_SEPARATOR = ', ';

const HIDDEN_MENU_ITEM = { disabled: true, hidden: true } as const;

/**
 * Univer Sheets UI plugin menu config: command IDs that are hidden and disabled in the ribbon/context menus.
 * @see https://docs.univer.ai/guides/sheets/features/core#menu-item-id-list
 */
export const UNIVER_SHEETS_UI_MENU_HIDDEN: Record<
  string,
  { disabled: boolean; hidden: boolean }
> = {
  'sheet.command.set-range-bold': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-italic': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-stroke': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-text-color': HIDDEN_MENU_ITEM,
  'sheet.command.set-background-color': HIDDEN_MENU_ITEM,
  'sheet.command.set-border-basic': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-font-family': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-underline': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-fontsize': HIDDEN_MENU_ITEM,
  'sheet.command.set-once-format-painter': HIDDEN_MENU_ITEM,
  'sheet.command.set-horizontal-text-align': HIDDEN_MENU_ITEM,
  'sheet.command.set-vertical-text-align': HIDDEN_MENU_ITEM,
  'sheet.command.set-text-wrap': HIDDEN_MENU_ITEM,
  'sheet.command.set-text-rotation': HIDDEN_MENU_ITEM,
  'sheet.operation.open.numfmt.panel': HIDDEN_MENU_ITEM,
  'sheet.command.numfmt.set.percent': HIDDEN_MENU_ITEM,
  'sheet.command.numfmt.set.currency': HIDDEN_MENU_ITEM,
  'sheet.command.add-range-protection-from-toolbar': HIDDEN_MENU_ITEM,
  'sheet.command.copy-formula-only': HIDDEN_MENU_ITEM,
  'sheet.command.paste-formula': HIDDEN_MENU_ITEM,
  'formula.command.insert-function': HIDDEN_MENU_ITEM,
  'sheets-formula.command.quick-sum': HIDDEN_MENU_ITEM,
  'formula-ui.operation.help-function': HIDDEN_MENU_ITEM,
  'formula-ui.operation.insert-function': HIDDEN_MENU_ITEM,
  'formula-ui.operation.more-functions': HIDDEN_MENU_ITEM,
  'formula-ui.operation.change-ref-to-absolute': HIDDEN_MENU_ITEM,
  'formula-ui.operation.search-function': HIDDEN_MENU_ITEM,
  'formula-ui.operation.select-editor-formula': HIDDEN_MENU_ITEM,
  'sheet.command.add-worksheet-merge': HIDDEN_MENU_ITEM,
  'sheet.command.add-worksheet-merge-all': HIDDEN_MENU_ITEM,
  'sheet.command.add-worksheet-merge-vertical': HIDDEN_MENU_ITEM,
  'sheet.command.add-worksheet-merge-horizontal': HIDDEN_MENU_ITEM,
  'sheet.command.remove-worksheet-merge': HIDDEN_MENU_ITEM,
  'sheet.command.move-rows': HIDDEN_MENU_ITEM,
  'sheet.command.sort-range-asc': HIDDEN_MENU_ITEM,
  'sheet.command.sort-range-desc': HIDDEN_MENU_ITEM,
  'sheet.command.sort-range-custom': HIDDEN_MENU_ITEM,
  'sheet.command.sort-range-asc-ctx': HIDDEN_MENU_ITEM,
  'sheet.command.sort-range-desc-ctx': HIDDEN_MENU_ITEM,
  'sheet.command.sort-range-custom-ctx': HIDDEN_MENU_ITEM,
  'sheet.menu.copy-special': HIDDEN_MENU_ITEM,
  'sheet.toolbar.text-to-number': HIDDEN_MENU_ITEM,
  'sheet.command.hide-row-confirm': HIDDEN_MENU_ITEM,
  'sheet.command.hide-col-confirm': HIDDEN_MENU_ITEM,
  'sheet.command.set-selected-rows-visible': HIDDEN_MENU_ITEM,
  'sheet.command.set-selected-cols-visible': HIDDEN_MENU_ITEM,
  'sheet.contextMenu.permission': HIDDEN_MENU_ITEM,
  'sheet.command.add-range-protection-from-context-menu': HIDDEN_MENU_ITEM,
  'sheet.command.set-range-protection-from-context-menu': HIDDEN_MENU_ITEM,
  'sheet.command.delete-range-protection-from-context-menu': HIDDEN_MENU_ITEM,
  'sheet.command.view-sheet-permission-from-context-menu': HIDDEN_MENU_ITEM,
  'sheet.menu.sheet-frozen': HIDDEN_MENU_ITEM,
  'sheet.column-header-menu.sheet-frozen': HIDDEN_MENU_ITEM,
  'sheet.row-header-menu.sheet-frozen': HIDDEN_MENU_ITEM,
  'sheet.command.set-selection-frozen': HIDDEN_MENU_ITEM,
  'sheet.command.set-row-frozen': HIDDEN_MENU_ITEM,
  'sheet.command.set-col-frozen': HIDDEN_MENU_ITEM,
  'sheet.command.set-first-row-frozen': HIDDEN_MENU_ITEM,
  'sheet.command.set-first-column-frozen': HIDDEN_MENU_ITEM,
  'sheet.command.cancel-frozen': HIDDEN_MENU_ITEM
};
